import {
  FingerWritingVariant,
  FingerWritingWord,
  TextOrderingUnit,
  TextOrderingUnitType,
  TextOrderingVariant,
  TextOrderingWord,
} from '../models/GameStore';

// interface FingerWritingCharacter {
//   character: string;
//   strokeCount?: number;
// }

export const getMaskedString = (
  gameVariants: TextOrderingVariant[] | FingerWritingVariant[],
): string => {
  const isTextOrderingVariant = (
    variants: TextOrderingVariant[] | FingerWritingVariant[],
  ): variants is TextOrderingVariant[] => {
    if (!Array.isArray(variants) || variants.length === 0) return false;
    const firstVariant = variants[0];
    return Boolean(
      firstVariant?.answer?.words?.[0] &&
        'units' in firstVariant.answer.words[0],
    );
  };

  const isNonLatin = (text: string): boolean => {
    // Remove newlines before testing
    const cleanText = text.replace(/\n/g, '');
    const latinRegex = /^[\u0020-\u007F\u00A0-\u00FF\u2026]*$/;
    return !latinRegex.test(cleanText);
  };

  const isSentence = (text: string): boolean => {
    return /[.!?。！？]$/.test(text.trim());
  };

  // Safe checks
  if (!Array.isArray(gameVariants) || gameVariants.length === 0) return '';
  const firstVariant = gameVariants[0];
  if (!firstVariant?.answer?.words) return '';

  // Handwriting variant handling
  if (!isTextOrderingVariant(gameVariants)) {
    const words = firstVariant.answer.words as FingerWritingWord[];
    let finalResult = '';

    for (const word of words) {
      if (!word?.characters?.length) continue;

      const text = word.characters.map((c) => c.character).join('');

      // If it's not a sentence, show the whole word
      if (!isSentence(text)) {
        finalResult += text;
        continue;
      }

      // Count how many characters are masked (excluding the last punctuation)
      const maskedCount = word.characters
        .slice(0, -1)
        .filter((char) => char.strokeCount !== undefined).length;
      const totalChars = word.characters.length - 1; // excluding last punctuation

      if (maskedCount === totalChars) {
        // If all characters are masked (except punctuation), show first + ... + last
        const firstChar = word.characters[0]?.character ?? '';
        const lastChar =
          word.characters[word.characters.length - 1]?.character ?? '';

        if (firstChar && lastChar) {
          finalResult += `${firstChar}...${lastChar}`;
        }
      } else {
        // For partial masking, show ... only for masked characters
        let wordResult = '';
        let lastWasMasked = false;

        word.characters.forEach((char) => {
          if (char.strokeCount !== undefined) {
            if (!lastWasMasked) {
              wordResult += '...';
              lastWasMasked = true;
            }
          } else {
            wordResult += char.character;
            lastWasMasked = false;
          }
        });

        finalResult += wordResult;
      }
    }

    return finalResult;
  }

  // Text Ordering variant handling
  const words = firstVariant.answer.words as TextOrderingWord[];
  let finalResult = '';

  for (const word of words) {
    const text = word.word;
    const units = word.units;

    if (!units?.length) continue;

    const isSignificantWord = (textString: string): boolean => {
      if (isNonLatin(textString)) {
        return true;
      }
      return /^[a-zA-Z0-9]{2,}$/.test(textString);
    };

    const allBlank = units.every((unit: TextOrderingUnit) => {
      if (!isSignificantWord(unit.key)) {
        return true;
      }
      return (
        unit.type === TextOrderingUnitType.Blank ||
        unit.type === TextOrderingUnitType.Candidate
      );
    });

    // Process each word based on its characteristics
    let wordResult = '';

    // Case 1: Single word, all blank
    if (!isSentence(text)) {
      if (allBlank) {
        if (isNonLatin(text)) {
          wordResult = text;
        } else {
          wordResult = '...' + text.slice(-1);
        }
        finalResult += wordResult;
        continue;
      }
    }

    // Case 2: Sentence with all blanks
    if (isSentence(text) && allBlank) {
      if (isNonLatin(text)) {
        wordResult = text[0] + '...' + text.slice(-1);
      } else {
        const trimmedText = text.trim();
        const wordsString = trimmedText.slice(0, -1).trim().split(/\s+/);

        if (wordsString.length === 1) {
          wordResult = text[0] + '...' + text.slice(-1);
        } else {
          wordResult = wordsString[0] + '...' + text.slice(-1);
        }
      }
      finalResult += wordResult;
      continue;
    }

    // Case 3: Japanese/Chinese text with partial blanks
    if (isNonLatin(text)) {
      let lastWasBlank = false;

      units.forEach((unit: TextOrderingUnit) => {
        if (
          unit.type === TextOrderingUnitType.Blank ||
          unit.type === TextOrderingUnitType.Candidate
        ) {
          if (!lastWasBlank) {
            wordResult += '...';
            lastWasBlank = true;
          }
        } else {
          wordResult += unit.key;
          lastWasBlank = false;
        }
      });
    } else {
      // Case 4: Latin text with partial blanks
      let currentWord = '';
      let isCollectingWord = false;

      units.forEach((unit: TextOrderingUnit, index: number) => {
        if (
          unit.type === TextOrderingUnitType.Blank ||
          unit.type === TextOrderingUnitType.Candidate
        ) {
          if (!isCollectingWord) {
            isCollectingWord = true;
            currentWord = unit.key;
          } else {
            currentWord += unit.key;
          }

          const nextUnit = units[index + 1];
          if (
            !nextUnit ||
            (nextUnit.type !== TextOrderingUnitType.Blank &&
              nextUnit.type !== TextOrderingUnitType.Candidate)
          ) {
            wordResult += '...' + currentWord.slice(-1);
            isCollectingWord = false;
            currentWord = '';
          }
        } else {
          wordResult += unit.key;
        }
      });
    }

    finalResult += wordResult;
  }

  return finalResult;
};
